import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";

export interface SaveDomAsPdfOptions {
  /** Elements matching this predicate are omitted from the raster (e.g. toolbar buttons). */
  ignoreElement?: (element: Element) => boolean;
  fileName?: string;
  /** Adjust cloned DOM before raster (e.g. expand overflow for full table capture). */
  onClone?: (clonedDoc: Document, element: HTMLElement) => void;
  /** Optional pixel size for the raster (use when children extend past overflow scrollports). */
  width?: number;
  height?: number;
}

function defaultFileName(): string {
  const d = new Date();
  const stamp = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  return `hallticket-result-review-${stamp}.pdf`;
}

/**
 * Renders a DOM subtree to canvas and builds a multi-page A4 PDF (landscape) when content is tall.
 */
export async function saveDomNodeAsPdf(element: HTMLElement, options?: SaveDomAsPdfOptions): Promise<void> {
  const fileName = options?.fileName ?? defaultFileName();

  const sizeOpts =
    typeof options?.width === "number"
    && typeof options?.height === "number"
    && Number.isFinite(options.width)
    && Number.isFinite(options.height)
    && options.width > 0
    && options.height > 0
      ? {
          width: Math.ceil(options.width),
          height: Math.ceil(options.height),
          windowWidth: Math.ceil(options.width),
          windowHeight: Math.ceil(options.height),
          scrollX: 0,
          scrollY: 0,
        }
      : {};

  const canvas = await html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    ignoreElements: options?.ignoreElement ?? (() => false),
    onclone: (clonedDoc: Document, el: HTMLElement) => {
      options?.onClone?.(clonedDoc, el);
    },
    ...sizeOpts,
  });

  const imgData = canvas.toDataURL("image/png");
  const pdf = new jsPDF({ unit: "mm", format: "a4", orientation: "landscape" });
  const pageW = pdf.internal.pageSize.getWidth();
  const pageH = pdf.internal.pageSize.getHeight();
  const margin = 8;
  const contentW = pageW - margin * 2;
  const contentH = pageH - margin * 2;
  const imgDisplayHeight = (canvas.height * contentW) / canvas.width;

  let heightLeft = imgDisplayHeight;
  let position = margin;

  pdf.addImage(imgData, "PNG", margin, position, contentW, imgDisplayHeight, undefined, "FAST");
  heightLeft -= contentH;

  while (heightLeft > 0.5) {
    position = margin - (imgDisplayHeight - heightLeft);
    pdf.addPage();
    pdf.addImage(imgData, "PNG", margin, position, contentW, imgDisplayHeight, undefined, "FAST");
    heightLeft -= contentH;
  }

  pdf.save(fileName);
}
